import { BRIEF_FIELD_LIMITS, sanitizeDownloadFileName, sanitizePlainTextInput } from '../../utils/inputSecurity';
import { BRIEF_TITLE, getOptionLabel } from './briefCopy';

export const TOTAL_STEPS = 6;

export const DEFAULT_BRIEF_FORM = {
  name: '',
  niche: '',
  product: '',
  usp: '',
  url: '',
  city: '',
  age: '',
  gender: 'OPTION_GENDER_ALL',
  interests: '',
  pain: '',
  triggers: '',
  objections: '',
  experience: 'OPTION_EXP_YES',
  platforms: [],
  worked: '',
  failed: '',
  pixel: 'OPTION_PIXEL_NO',
  budget: 'OPTION_BUDGET_MID',
  maxCpl: '',
  scaleReady: 'OPTION_SCALE_YES',
  creativeBudget: 'OPTION_CREATIVE_NO',
  goal: 'OPTION_GOAL_LEADS',
  monthlyTarget: '',
  averageCheck: '',
  margin: '',
  kpis: [],
  deadline: 'OPTION_DEADLINE_2W',
  contact: '',
  messenger: 'OPTION_MESSENGER_WA',
  materials: [],
  landing: 'OPTION_LANDING_IG',
  extra: '',
};

export const GENDER_OPTIONS = ['OPTION_GENDER_F', 'OPTION_GENDER_M', 'OPTION_GENDER_ALL'];
export const EXPERIENCE_OPTIONS = ['OPTION_EXP_NO', 'OPTION_EXP_YES'];
export const PLATFORM_OPTIONS = [
  'PLATFORM_IG',
  'PLATFORM_TT',
  'PLATFORM_YT',
  'PLATFORM_VK',
  'PLATFORM_GG',
  'PLATFORM_YD',
];
export const PIXEL_OPTIONS = ['OPTION_PIXEL_YES', 'OPTION_PIXEL_NO', 'OPTION_PIXEL_UNKNOWN'];
export const BUDGET_OPTIONS = [
  'OPTION_BUDGET_LOW',
  'OPTION_BUDGET_MID',
  'OPTION_BUDGET_MID_HIGH',
  'OPTION_BUDGET_HIGH',
  'OPTION_BUDGET_TOP',
];
export const SCALE_OPTIONS = ['OPTION_SCALE_YES', 'OPTION_SCALE_NO', 'OPTION_SCALE_MAYBE'];
export const CREATIVE_OPTIONS = ['OPTION_CREATIVE_YES', 'OPTION_CREATIVE_NO', 'OPTION_CREATIVE_DISCUSS'];
export const GOAL_OPTIONS = [
  'OPTION_GOAL_LEADS',
  'OPTION_GOAL_SALES',
  'OPTION_GOAL_SUBS',
  'OPTION_GOAL_BRAND',
  'OPTION_GOAL_VISIT',
];
export const KPI_OPTIONS = [
  'KPI_CPL',
  'KPI_LEADS',
  'KPI_ROMI',
  'KPI_SALES',
  'KPI_REACH',
  'KPI_SUBS',
];
export const DEADLINE_OPTIONS = [
  'OPTION_DEADLINE_2W',
  'OPTION_DEADLINE_1M',
  'OPTION_DEADLINE_3M',
  'OPTION_DEADLINE_NONE',
];
export const MESSENGER_OPTIONS = [
  'OPTION_MESSENGER_WA',
  'OPTION_MESSENGER_TG',
  'OPTION_MESSENGER_IG',
  'OPTION_MESSENGER_EMAIL',
];
export const MATERIAL_OPTIONS = [
  'MAT_PHOTO',
  'MAT_VIDEO',
  'MAT_BRAND',
  'MAT_TEXT',
  'MAT_NONE',
];
export const LANDING_OPTIONS = [
  'OPTION_LANDING_SITE',
  'OPTION_LANDING_IG',
  'OPTION_LANDING_FORM',
  'OPTION_LANDING_WA',
  'OPTION_LANDING_NEW',
];

const DASH = '—';

const formatText = (value, maxLength = 2000) => {
  const trimmed = sanitizePlainTextInput(value, maxLength).trim();
  return trimmed || DASH;
};

const formatChoice = (value) => (value ? getOptionLabel(value) : DASH);

const formatMulti = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return DASH;
  }

  return values.map((entry) => getOptionLabel(entry)).join(', ');
};

export const buildBriefViewModel = (form) => {
  const sections = [
    {
      title: 'О бизнесе',
      rows: [
        { label: 'Название / бренд', value: formatText(form.name, BRIEF_FIELD_LIMITS.name) },
        { label: 'Ниша / сфера', value: formatText(form.niche, BRIEF_FIELD_LIMITS.niche) },
        { label: 'Продукт / услуга', value: formatText(form.product, BRIEF_FIELD_LIMITS.product) },
        { label: 'Конкурентное преимущество (УТП)', value: formatText(form.usp, BRIEF_FIELD_LIMITS.usp) },
        { label: 'Сайт / Instagram', value: formatText(form.url, BRIEF_FIELD_LIMITS.url) },
        { label: 'География', value: formatText(form.city, BRIEF_FIELD_LIMITS.city) },
      ],
    },
    {
      title: 'Целевая аудитория',
      rows: [
        { label: 'Возраст', value: formatText(form.age, BRIEF_FIELD_LIMITS.age) },
        { label: 'Пол', value: formatChoice(form.gender) },
        { label: 'Интересы и образ жизни', value: formatText(form.interests, BRIEF_FIELD_LIMITS.interests) },
        { label: 'Главная боль клиента', value: formatText(form.pain, BRIEF_FIELD_LIMITS.pain) },
        { label: 'Триггеры к покупке', value: formatText(form.triggers, BRIEF_FIELD_LIMITS.triggers) },
        { label: 'Возражения', value: formatText(form.objections, BRIEF_FIELD_LIMITS.objections) },
      ],
    },
    {
      title: 'Опыт с рекламой',
      rows: [
        { label: 'Опыт запуска рекламы', value: formatChoice(form.experience) },
        { label: 'Использовали платформы', value: formatMulti(form.platforms) },
        { label: 'Что работало хорошо', value: formatText(form.worked, BRIEF_FIELD_LIMITS.worked) },
        { label: 'Что не сработало', value: formatText(form.failed, BRIEF_FIELD_LIMITS.failed) },
        { label: 'Пиксель / аналитика', value: formatChoice(form.pixel) },
      ],
    },
    {
      title: 'Бюджет',
      rows: [
        { label: 'Ежемесячный рекламный бюджет', value: formatChoice(form.budget) },
        { label: 'Максимально допустимый CPL', value: formatText(form.maxCpl, BRIEF_FIELD_LIMITS.maxCpl) },
        { label: 'Готовность масштабировать', value: formatChoice(form.scaleReady) },
        { label: 'Бюджет на креативы', value: formatChoice(form.creativeBudget) },
      ],
    },
    {
      title: 'Цели и KPI',
      rows: [
        { label: 'Главная цель рекламы', value: formatChoice(form.goal) },
        { label: 'Целевой результат в месяц', value: formatText(form.monthlyTarget, BRIEF_FIELD_LIMITS.monthlyTarget) },
        { label: 'Средний чек', value: formatText(form.averageCheck, BRIEF_FIELD_LIMITS.averageCheck) },
        { label: 'Маржинальность', value: formatText(form.margin, BRIEF_FIELD_LIMITS.margin) },
        { label: 'KPI для оценки работы', value: formatMulti(form.kpis) },
        { label: 'Срок первого результата', value: formatChoice(form.deadline) },
      ],
    },
    {
      title: 'Процесс работы',
      rows: [
        { label: 'Контактное лицо', value: formatText(form.contact, BRIEF_FIELD_LIMITS.contact) },
        { label: 'Мессенджер для связи', value: formatChoice(form.messenger) },
        { label: 'Готовые материалы', value: formatMulti(form.materials) },
        { label: 'Посадочная страница', value: formatChoice(form.landing) },
        { label: 'Дополнительно', value: formatText(form.extra, BRIEF_FIELD_LIMITS.extra) },
      ],
    },
  ];

  return sections;
};

export const buildBriefPlainText = (sections, title = BRIEF_TITLE) => {
  let text = `${title}\n`;
  text += `${'═'.repeat(40)}\n\n`;

  sections.forEach((section) => {
    text += `${section.title.toUpperCase()}\n`;
    text += `${'─'.repeat(30)}\n`;
    section.rows.forEach((row) => {
      text += `${row.label}: ${row.value}\n`;
    });
    text += '\n';
  });

  return text;
};

export const buildBriefPlainTextLocalized = (sections, title = BRIEF_TITLE) =>
  buildBriefPlainText(sections, title);

export const getProgressPercent = (step) => ((step - 1) / TOTAL_STEPS) * 100;

export const createBriefFileName = (form) => {
  const base = sanitizeDownloadFileName(form.name);
  return `${base}.docx`;
};
